import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output } from "@angular/core";
import { ErrorClass, Field, PayoutProvider, ValidationError } from "@models";
import { PayAccountService } from "@main-services/pay_account.service";
import { UserService } from "@main-services/user.service";
import { FiatService } from "@main-services/currency/fiat.service";
import { Router } from "@angular/router";
import { PaymentAccount } from "@models/payment_account";
import { empty, equal } from "@helpers/utils";
import { PayoutAccountDetailTileComponent } from "../payout-account-detail-tile/payout-account-detail-tile.component";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ConfirmModalService } from "@core-services/confirm-modal.service";

@UntilDestroy()
@Component({
	selector: "payment-account-detail",
	templateUrl: "./payment-account-detail-tile.component.html",
	styleUrls: ["./payment-account-detail-tile.component.scss"],
	standalone: false
})
export class PaymentAccountDetailTileComponent
	extends PayoutAccountDetailTileComponent
	implements AfterViewInit, OnDestroy
{
	@Input()
	public account: PaymentAccount = null;

	@Output()
	// eslint-disable-next-line @angular-eslint/no-output-native
	public finish = new EventEmitter<PaymentAccount>();

	@Output()
	public onReturn = new EventEmitter<void>();

	public constructor(
		protected infoService: InfoService,
		protected fiatService: FiatService,
		protected translationService: TranslationService,
		protected masterdataService: MasterdataService,
		protected userService: UserService,
		protected profileFacade: NewProfileFacade,
		protected modalService: ModalService,
		protected confirmModalService: ConfirmModalService,
		protected payService: PayAccountService,
		protected router: Router,
		protected apicallService: ApicallService
	) {
		super(
			infoService,
			fiatService,
			translationService,
			masterdataService,
			userService,
			profileFacade,
			modalService,
			confirmModalService,
			payService,
			router,
			apicallService
		);

		this.account = new PaymentAccount();
		const sepaPayoutProvider: PayoutProvider = this.masterdataService.payout_providers.find((p: PayoutProvider) =>
			this.masterdataService.isSepaProvider(p)
		);
		this.account.setProviderId(sepaPayoutProvider.getId());
		this.account.setFiatId("1"); // Only for EURO
		this.account.setFields(sepaPayoutProvider.getFields());
	}

	public ngAfterViewInit(): void {
		if (this.account) {
			if (this.getUserNameField() && empty(this.getUserNameField().getValue())) {
				this.profileFacade
					.getProfile$()
					.pipe(untilDestroyed(this))
					.subscribe((profile) => {
						const name = profile.fullName;
						this.getUserNameField().setValue(name);
					});
			}
		}
	}

	public copyPasteValidation(event: FocusEvent): void {
		if (this.accountValidated) {
			this.setBankFieldData();
			this.accountValidated = false;
		}

		if (equal(event["inputType"], "insertFromPaste")) {
			this.onInputFocusOut(event, this.getBankIbanField());
		}
	}

	public ngOnDestroy(): void {
		this.getFields().forEach((f: Field) => f.setValue(""));
	}

	public getUserNameField(): Field {
		return this.getFields().find((f: Field) => f.getName() === "payout_receiver_name");
	}

	public getBankIbanField(): Field {
		return this.getFields().find((f: Field) => f.getName() === "iban");
	}

	public showBankDataField(): boolean {
		return this.getBankDataFields().some((f: Field) => !empty(f.getValue()));
	}

	public getBankDataFields(): Field[] {
		return this.getFields().filter((f: Field) => f.getName() === "bank_name" || f.getName() === "bic");
	}

	public getFieldName(f: Field): string {
		if (f.getName() === "payout_receiver_name") {
			return "name";
		}
		return f.getName() + "_primary_field";
	}

	public readonly(f: Field): boolean {
		return f.getName() === "payout_receiver_name";
	}

	public get isAccountValidated(): boolean {
		return !this.accountValidated;
	}

	public needsBicTag(f: Field): boolean {
		return !empty(f.getValue()) && f.getName() === "bic";
	}

	public isSepaSupported(): boolean {
		return this.sepaSupported;
	}

	public backToProviders(): void {
		this.onReturn.emit();
	}

	public createSepaAccount(): void {
		const option: ApicallOption = new ApicallOption(PaymentAccount);
		option.data = {
			iban: this.account
				.getFields()
				.find((f) => f.getName() === "iban")
				.getValue(),
			bic: this.account
				.getFields()
				.find((f) => f.getName() === "bic")
				.getValue(),
			bank_name: this.account
				.getFields()
				.find((f) => f.getName() === "bank_name")
				.getValue(),
			holder: this.account
				.getFields()
				.find((f) => f.getName() === "payout_receiver_name")
				.getValue()
		};

		this.apicallService.post("user/sepa-mandates", option).subscribe(
			(response: PaymentAccount[]) => {
				this.finish.emit(response[0]);
			},
			(error: Error) => {
				if (error instanceof ErrorClass) {
					if (error instanceof ValidationError && !error.containsCode("sepa_mandate.bank_account.used")) {
						this.account.setValidationErrors(error);
					} else {
						this.infoService.setError(error);
					}
				} else {
					console.error(error);
				}
			}
		);
	}
}
